import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { orgTreeApi } from '@/services/org-tree'
import { useAuthStore } from '@/stores/auth-store'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'

const firstText = (...values: unknown[]) => {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text) return text
  }
  return null
}

export function useStoreOrgScope() {
  const location = useLocation()
  const serviceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const effectiveServiceInstanceId = getServiceInstanceIdFromSearch(location.search) ?? serviceInstanceId ?? null

  const ancestorRootQuery = useQuery({
    queryKey: ['store-org-scope-root', effectiveServiceInstanceId],
    queryFn: () => orgTreeApi.findAncestorRoot(effectiveServiceInstanceId!),
    enabled: Boolean(effectiveServiceInstanceId),
    staleTime: 120_000,
  })

  const currentInstanceQuery = useQuery({
    queryKey: ['store-org-scope-current', effectiveServiceInstanceId],
    queryFn: () => orgTreeApi.getOptionalServiceInstance(effectiveServiceInstanceId!),
    enabled: Boolean(effectiveServiceInstanceId),
    staleTime: 120_000,
  })

  const orgInstanceId = useMemo(() => {
    const current = (currentInstanceQuery.data ?? {}) as Record<string, unknown>
    const root = (ancestorRootQuery.data ?? {}) as Record<string, unknown>

    return firstText(
      current.org_service_instance_instance_id,
      current.org_service_instance_id,
      current.org_instance_instance_id,
      current.org_instance_id,
      current.org_instance,
      root.instance_id,
      root['instance-id'],
      effectiveServiceInstanceId,
    )
  }, [ancestorRootQuery.data, currentInstanceQuery.data, effectiveServiceInstanceId])

  return {
    serviceInstanceId: effectiveServiceInstanceId,
    orgInstanceId,
    isLoading: ancestorRootQuery.isLoading || currentInstanceQuery.isLoading,
  }
}
